import React, { useState, useEffect } from 'react';
import {
  Building2,
  Mail,
  Phone,
  Calendar,
  Save,
  Loader,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { withMask } from 'use-mask-input'
import { CompanyProfile } from '../../types';
import api from '../../services/api';
import { LoadingState } from '../../components/LoadingSpinner';
import Alert from '../../components/Alert';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const CompanySettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<CompanyProfile>('/company/profile');
      setProfile(response.data);
      setFormData({
        name: response.data.name,
        email: response.data.email || '',
        phone: response.data.phone || '',
      });
    } catch (err: any) {
      console.error('Error fetching company profile:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados da empresa');
      toast.error('Erro ao carregar dados da empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isOwner) {
      toast.error('Apenas o proprietário pode atualizar os dados da empresa');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Nome da empresa é obrigatório');
      return;
    }

    if(formData.phone) {
      formData.phone = formData.phone.replace(/\D/g, '');
    }

    try {
      setSaving(true);
      const response = await api.put('/company/profile', formData);
      setProfile(prev => prev ? { ...prev, ...response.data.company } : null);
      toast.success('Dados da empresa atualizados com sucesso!');
    } catch (err: any) {
      console.error('Error updating company:', err);
      toast.error(err.response?.data?.error || 'Erro ao atualizar dados da empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return <LoadingState message="Carregando dados da empresa..." />;
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert type="error" message={error} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6">
        <Alert type="error" message="Dados da empresa não encontrados" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Configurações da Empresa
        </h1>
        <p className="text-gray-600">
          Gerencie as informações da sua empresa
        </p>
      </div>

      {/* Warning for non-owners */}
      {!isOwner && (
        <div className="mb-6">
          <Alert
            type="warning"
            message="Apenas o proprietário da empresa pode editar estas configurações. Você pode visualizar as informações abaixo."
          />
        </div>
      )}

      <div className="space-y-6">
        {/* Company Information Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 size={20} />
            Informações da Empresa
          </h2>

          <form onSubmit={handleSave} className="space-y-4">
            {/* Company Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Nome da Empresa *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={!isOwner}
                required
                maxLength={255}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  !isOwner ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                placeholder="Nome da sua empresa"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <Mail size={14} />
                Email de Contato
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={!isOwner}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  !isOwner ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                placeholder="contato@empresa.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                <Phone size={14} />
                Telefone
              </label>
              <input
                type="tel"
                id="phone"
                ref={withMask('(99) 99999-9999')}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={!isOwner}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  !isOwner ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                placeholder="+55 11 99999-9999"
              />
            </div>

            {/* Save Button */}
            {isOwner && (
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Salvar Alterações
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Subscription Info (Read-only) */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar size={20} />
            Assinatura Atual
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Plano</p>
              <p className="text-lg font-semibold text-gray-900">{profile.plan.name}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Status</p>
              <div className="flex items-center gap-2">
                {profile.subscription.status === 'active' ? (
                  <>
                    <CheckCircle size={16} className="text-green-600" />
                    <span className="text-lg font-semibold text-green-600">Ativo</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={16} className="text-yellow-600" />
                    <span className="text-lg font-semibold text-yellow-600 capitalize">
                      {profile.subscription.status}
                    </span>
                  </>
                )}
              </div>
            </div>

            {profile.subscription.starts_at && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Início</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(profile.subscription.starts_at)}
                </p>
              </div>
            )}

            {profile.subscription.ends_at && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Renovação</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(profile.subscription.ends_at)}
                </p>
              </div>
            )}
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <p>
              Para fazer upgrade, downgrade ou cancelar sua assinatura, acesse a{' '}
              <a href="/admin/subscription" className="text-indigo-600 hover:text-indigo-700 font-medium">
                página de assinatura
              </a>
              .
            </p>
          </div>
        </div>

        {/* Company Metadata */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Informações do Sistema
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">ID da Empresa:</span>
              <p className="font-mono text-gray-900 mt-1">{profile.id}</p>
            </div>

            <div>
              <span className="text-gray-600">Data de Criação:</span>
              <p className="text-gray-900 mt-1">{formatDate(profile.created_at)}</p>
            </div>

            <div>
              <span className="text-gray-600">Última Atualização:</span>
              <p className="text-gray-900 mt-1">{formatDate(profile.updated_at)}</p>
            </div>

            {profile.subscription.kiwify_email && (
              <div>
                <span className="text-gray-600">Email Kiwify:</span>
                <p className="text-gray-900 mt-1">{profile.subscription.kiwify_email}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettingsPage;
